/**
 * ConnectedDevicesPanel — user's active sessions inventory (Settings).
 *
 * Backend: /api/user/devices (GET/DELETE), /api/user/devices/all-others (POST).
 * Each session was created on login/verify with UA / IP / device parsing.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Smartphone, Monitor, Laptop, Tablet, X, Loader2, LogOut, ShieldCheck, RefreshCw,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';


const DEVICES_I18N = {
  fr: { title: 'Appareils connectés', subtitle: "Vois où ton compte est actif. Si tu ne reconnais pas un appareil, déconnecte-le immédiatement.", active: 'actif(s)', revokeAll: 'Déconnecter les autres', thisDevice: 'Cet appareil', lastSeen: 'Vu', since: 'depuis', confirmRevoke: 'Déconnecter cet appareil ?', revoked: 'Appareil déconnecté', confirmRevokeAll: 'Déconnecter tous les autres appareils ?', revokedN: 'Appareils déconnectés', empty: 'Aucun appareil actif.', revoke: 'Déconnecter' },
  en: { title: 'Connected devices', subtitle: "See where your account is active. If you don't recognize a device, sign it out immediately.", active: 'active', revokeAll: 'Sign out others', thisDevice: 'This device', lastSeen: 'Seen', since: 'since', confirmRevoke: 'Sign out this device?', revoked: 'Device signed out', confirmRevokeAll: 'Sign out all other devices?', revokedN: 'Devices signed out', empty: 'No active devices.', revoke: 'Sign out' },
  es: { title: 'Dispositivos conectados', subtitle: 'Mira dónde está activa tu cuenta. Si no reconoces un dispositivo, desconéctalo de inmediato.', active: 'activo(s)', revokeAll: 'Desconectar los demás', thisDevice: 'Este dispositivo', lastSeen: 'Visto', since: 'desde', confirmRevoke: '¿Desconectar este dispositivo?', revoked: 'Dispositivo desconectado', confirmRevokeAll: '¿Desconectar todos los demás dispositivos?', revokedN: 'Dispositivos desconectados', empty: 'Ningún dispositivo activo.', revoke: 'Desconectar' },
  ar: { title: 'الأجهزة المتصلة', subtitle: 'اطّلع على أماكن نشاط حسابك. إذا لم تتعرف على جهاز، فافصله فورًا.', active: 'نشط', revokeAll: 'فصل الأجهزة الأخرى', thisDevice: 'هذا الجهاز', lastSeen: 'شوهد', since: 'منذ', confirmRevoke: 'فصل هذا الجهاز؟', revoked: 'تم فصل الجهاز', confirmRevokeAll: 'فصل جميع الأجهزة الأخرى؟', revokedN: 'تم فصل الأجهزة', empty: 'لا توجد أجهزة نشطة.', revoke: 'فصل' },
  pt: { title: 'Dispositivos conectados', subtitle: 'Veja onde sua conta está ativa. Se não reconhecer um dispositivo, desconecte-o imediatamente.', active: 'ativo(s)', revokeAll: 'Desconectar os outros', thisDevice: 'Este dispositivo', lastSeen: 'Visto', since: 'desde', confirmRevoke: 'Desconectar este dispositivo?', revoked: 'Dispositivo desconectado', confirmRevokeAll: 'Desconectar todos os outros dispositivos?', revokedN: 'Dispositivos desconectados', empty: 'Nenhum dispositivo ativo.', revoke: 'Desconectar' },
  de: { title: 'Verbundene Geräte', subtitle: 'Sieh, wo dein Konto aktiv ist. Wenn du ein Gerät nicht erkennst, melde es sofort ab.', active: 'aktiv', revokeAll: 'Andere abmelden', thisDevice: 'Dieses Gerät', lastSeen: 'Gesehen', since: 'seit', confirmRevoke: 'Dieses Gerät abmelden?', revoked: 'Gerät abgemeldet', confirmRevokeAll: 'Alle anderen Geräte abmelden?', revokedN: 'Geräte abgemeldet', empty: 'Keine aktiven Geräte.', revoke: 'Abmelden' },
  ru: { title: 'Подключённые устройства', subtitle: 'Смотрите, где активен ваш аккаунт. Если не узнаёте устройство — немедленно отключите его.', active: 'активно', revokeAll: 'Отключить остальные', thisDevice: 'Это устройство', lastSeen: 'Был', since: 'с', confirmRevoke: 'Отключить это устройство?', revoked: 'Устройство отключено', confirmRevokeAll: 'Отключить все остальные устройства?', revokedN: 'Устройства отключены', empty: 'Нет активных устройств.', revoke: 'Отключить' },
  zh: { title: '已连接的设备', subtitle: '查看您的帐户在哪些设备上处于活动状态。如果发现陌生设备，请立即将其退出。', active: '个活动', revokeAll: '退出其他设备', thisDevice: '此设备', lastSeen: '上次活动', since: '自', confirmRevoke: '退出此设备？', revoked: '设备已退出', confirmRevokeAll: '退出所有其他设备？', revokedN: '设备已退出', empty: '没有活动的设备。', revoke: '退出' },
  ja: { title: '接続中のデバイス', subtitle: 'アカウントがアクティブな場所を確認できます。見覚えのないデバイスがあれば、すぐにサインアウトしてください。', active: '台アクティブ', revokeAll: '他のデバイスをサインアウト', thisDevice: 'このデバイス', lastSeen: '最終アクセス', since: '開始日', confirmRevoke: 'このデバイスをサインアウトしますか？', revoked: 'デバイスをサインアウトしました', confirmRevokeAll: '他のすべてのデバイスをサインアウトしますか？', revokedN: 'デバイスをサインアウトしました', empty: 'アクティブなデバイスはありません。', revoke: 'サインアウト' },
  hi: { title: 'कनेक्टेड डिवाइस', subtitle: 'देखें कि आपका खाता कहाँ सक्रिय है। यदि कोई डिवाइस पहचान में न आए, तो उसे तुरंत डिस्कनेक्ट करें।', active: 'सक्रिय', revokeAll: 'अन्य को डिस्कनेक्ट करें', thisDevice: 'यह डिवाइस', lastSeen: 'देखा गया', since: 'से', confirmRevoke: 'इस डिवाइस को डिस्कनेक्ट करें?', revoked: 'डिवाइस डिस्कनेक्ट किया गया', confirmRevokeAll: 'अन्य सभी डिवाइस डिस्कनेक्ट करें?', revokedN: 'डिवाइस डिस्कनेक्ट किए गए', empty: 'कोई सक्रिय डिवाइस नहीं।', revoke: 'डिस्कनेक्ट' },
  nl: { title: 'Verbonden apparaten', subtitle: 'Zie waar je account actief is. Herken je een apparaat niet, meld het dan direct af.', active: 'actief', revokeAll: 'Andere afmelden', thisDevice: 'Dit apparaat', lastSeen: 'Gezien', since: 'sinds', confirmRevoke: 'Dit apparaat afmelden?', revoked: 'Apparaat afgemeld', confirmRevokeAll: 'Alle andere apparaten afmelden?', revokedN: 'Apparaten afgemeld', empty: 'Geen actieve apparaten.', revoke: 'Afmelden' },
  sw: { title: 'Vifaa vilivyounganishwa', subtitle: 'Angalia mahali akaunti yako inatumika. Usipotambua kifaa, kiondoe mara moja.', active: 'hai', revokeAll: 'Ondoa vingine', thisDevice: 'Kifaa hiki', lastSeen: 'Imeonekana', since: 'tangu', confirmRevoke: 'Ondoa kifaa hiki?', revoked: 'Kifaa kimeondolewa', confirmRevokeAll: 'Ondoa vifaa vingine vyote?', revokedN: 'Vifaa vimeondolewa', empty: 'Hakuna kifaa hai.', revoke: 'Ondoa' },
  ht: { title: 'Aparèy konekte yo', subtitle: 'Gade kote kont ou aktif. Si ou pa rekonèt yon aparèy, dekonekte li imedyatman.', active: 'aktif', revokeAll: 'Dekonekte lòt yo', thisDevice: 'Aparèy sa a', lastSeen: 'Wè', since: 'depi', confirmRevoke: 'Dekonekte aparèy sa a ?', revoked: 'Aparèy dekonekte', confirmRevokeAll: 'Dekonekte tout lòt aparèy yo ?', revokedN: 'Aparèy dekonekte', empty: 'Pa gen aparèy aktif.', revoke: 'Dekonekte' },
};

const iconFor = (device) => {
  const d = (device || '').toLowerCase();
  if (d.includes('iphone') || d.includes('android phone')) return Smartphone;
  if (d.includes('ipad') || d.includes('tablet')) return Tablet;
  if (d.includes('mac') || d.includes('linux')) return Laptop;
  return Monitor;
};

const relTime = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} j`;
};

const ConnectedDevicesPanel = () => {
  const { language } = useLanguage();
  const dt = (k) => (DEVICES_I18N[language] || DEVICES_I18N.en)[k];
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/user/devices');
      setDevices(r.data?.devices || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (d) => {
    if (!window.confirm(dt('confirmRevoke') || 'Déconnecter cet appareil ?')) return;
    setBusy(d.id);
    try {
      await axios.delete(`/api/user/devices/${d.id}`);
      toast.success(dt('revoked') || 'Appareil déconnecté');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(null);
    }
  };

  const revokeAll = async () => {
    if (!window.confirm(dt('confirmRevokeAll') || 'Déconnecter tous les autres appareils ?')) return;
    setBusy('all');
    try {
      const r = await axios.post('/api/user/devices/all-others');
      toast.success((dt('revokedN') || 'Appareils déconnectés').replace('{n}', r.data?.revoked || 0));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(null);
    }
  };

  const others = devices.filter(d => !d.current);

  return (
    <div className="space-y-3" data-testid="devices-panel">
      <div className="rounded-2xl bg-gradient-to-br from-indigo-500/10 to-emerald-500/10 border border-indigo-400/30 p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-indigo-300 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-bold text-white mb-1">{dt('title') || 'Appareils connectés'}</div>
            <p className="text-white/70">{dt('subtitle') || 'Vois où ton compte est actif. Si tu ne reconnais pas un appareil, déconnecte-le immédiatement.'}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-white/60">{devices.length} {dt('active') || 'actif(s)'}</span>
        <div className="flex gap-2">
          <button onClick={load} className="p-1.5 rounded-lg hover:bg-white/10 text-white/60" data-testid="devices-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {others.length > 0 && (
            <button
              onClick={revokeAll}
              disabled={busy === 'all'}
              className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/40 border border-red-400/40 text-red-200 text-xs font-semibold flex items-center gap-1.5"
              data-testid="devices-revoke-all-btn"
            >
              {busy === 'all' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
              {dt('revokeAll') || 'Déconnecter les autres'}
            </button>
          )}
        </div>
      </div>

      {loading && devices.length === 0 ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-white/50" /></div>
      ) : devices.length === 0 ? (
        <div className="text-center py-8 text-white/40 border border-dashed border-white/10 rounded-xl">
          {dt('empty') || 'Aucun appareil actif.'}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="devices-list">
          {devices.map(d => {
            const Icon = iconFor(d.ua_device);
            return (
              <li
                key={d.id}
                className={`rounded-xl border p-3 flex items-center gap-3 ${d.current ? 'bg-emerald-500/10 border-emerald-400/40' : 'bg-white/5 border-white/10'}`}
                data-testid={`device-${d.id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${d.current ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white/70'}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white text-sm">{d.ua_device || 'Unknown'}</span>
                    {d.current && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/25 text-emerald-200 font-medium">
                        {dt('thisDevice') || 'Cet appareil'}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/50 mt-0.5">
                    {d.ua_os} · {d.ua_browser} · {d.ip || '—'}
                  </div>
                  <div className="text-[11px] text-white/40 mt-0.5">
                    {dt('lastSeen') || 'Vu'}: il y a {relTime(d.last_seen_at)} · {dt('since') || 'depuis'} {new Date(d.created_at).toLocaleDateString()}
                  </div>
                </div>
                {!d.current && (
                  <button
                    onClick={() => revoke(d)}
                    disabled={busy === d.id}
                    className="p-2 rounded-lg hover:bg-red-500/20 text-red-300 flex-shrink-0 disabled:opacity-40"
                    title={dt('revoke') || 'Déconnecter'}
                    data-testid={`device-revoke-${d.id}`}
                  >
                    {busy === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ConnectedDevicesPanel;
